import { Text, SegmentedControl, Tooltip } from "@mantine/core";
import Markdown from "./Markdown";
import JsonBlock from "./JsonBlock";

/**
 * The three ways model-written text is shown across the app: rendered Markdown
 * (what the model meant), raw text (what it actually emitted, whitespace and
 * all) and monospace (what it looks like on the wire).
 */
const TEXT_DISPLAY_MODES = [
  { label: "Markdown", value: "markdown" },
  { label: "Raw", value: "raw" },
  { label: "Mono", value: "mono" },
];

/** The 3-state switch that drives {@link RenderedText}. */
export function TextDisplayModeControl({
  value,
  onChange,
  size = "xs",
  label = "How the model's text is rendered",
  ...rest
}) {
  return (
    <Tooltip label={label} position="left" withArrow>
      <SegmentedControl
        value={value}
        onChange={onChange}
        size={size}
        data={TEXT_DISPLAY_MODES}
        {...rest}
      />
    </Tooltip>
  );
}

/**
 * One piece of model-written text in the currently selected display mode.
 * `size` / `c` are the Mantine Text props and are inherited by the rendered
 * Markdown, so a dimmed, small transcript entry stays dimmed and small.
 */
export default function RenderedText({ text, mode = "markdown", size, c, style }) {
  if (!text) return null;

  if (mode === "mono")
    return <JsonBlock value={text} style={{ wordBreak: "break-word", ...style }} />;

  if (mode === "markdown")
    return (
      <Text
        component="div"
        size={size}
        c={c}
        className="rendered-markdown"
        style={{ wordBreak: "break-word", ...style }}
      >
        <Markdown>{text}</Markdown>
      </Text>
    );

  return (
    <Text
      size={size}
      c={c}
      style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", ...style }}
    >
      {text}
    </Text>
  );
}
